import React, { useEffect, useRef } from "react";
import intl from "react-intl-universal";

import { Grid } from "../../../../../components/Grid";
import { useInput } from "../../../../../hooks/useInput";
import { Spacer } from "../../../../../components/Spacer";
import { Spinner } from "../../../../../components/Spinner";
import { Checkbox } from "../../../../../components/Checkbox";
import { useOnChange } from "../../../../../hooks/useOnChange";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { InputText } from "../../../../../components/InputText";
import { InputNumber } from "../../../../../components/InputNumber";
import { SelectSingle } from "../../../../../components/SelectSingle";

export const CalcSettingsForm = props => {

    const { imageSetting, sliceNum, fieldType, calcAreaName, disabled, fMin, fMax } = props;

    const isChangeableFieldType = fieldType === "EXHAUSTION_FACTOR_BLMSCHV";

    const slice = useInput("slice", imageSetting.slice);
    const fieldMax = useInput("fieldMax", imageSetting.fieldMax);
    const colorScale = useInput("colorScale", imageSetting.colorScale);
    const marker = useInput("marker", imageSetting.marker);
    const grid = useInput("grid", imageSetting.grid);
    const logScale = useInput("logScale", imageSetting.logScale);
    const colorScaleLowerLimit = useInput("colorScaleLowerLimit", imageSetting.colorScaleLowerLimit);
    const colorScaleUpperLimit = useInput("colorScaleUpperLimit", imageSetting.colorScaleUpperLimit);
    const colorScaleAuto = useInput("colorScaleAuto", imageSetting.colorScaleAuto);
    const changeableFieldType = useInput("fieldType", fieldType);

    const upperLimitRef = useRef("");
    const lowerLimitRef = useRef("");

    useOnChange((name, value) => {
        if (value === "PROTECTION_AREA_BLMSCHV") {
            upperLimitRef.current = colorScaleUpperLimit.value;
            lowerLimitRef.current = colorScaleLowerLimit.value;
            colorScaleUpperLimit.set(colorScaleUpperLimit.value >= 1 ? 1 : 0);
            colorScaleLowerLimit.set(0);
        } else if (value === "EXHAUSTION_FACTOR_BLMSCHV") {
            colorScaleUpperLimit.set(upperLimitRef.current);
            colorScaleLowerLimit.set(lowerLimitRef.current);
        }
    }, [changeableFieldType]);

    useEffect(() => {
        if (fMin !== undefined && fMax !== undefined) {
            setColorScaleLimits();
        }
    }, [fMin, fMax]);

    useOnChange((name, value, ignore) => {
        if (ignore !== true) {
            if (["slice", "colorScaleLowerLimit", "colorScaleUpperLimit"].includes(name)) {
                debouncedOnChange(name, value);
            } else {
                onChange(name, value);
            }
        }
    }, [slice, fieldMax, colorScale, marker, grid, logScale, colorScaleAuto, colorScaleLowerLimit, colorScaleUpperLimit]);

    useOnChange((name, value) => {
        props.onChangeFieldType(value);
    }, [changeableFieldType]);

    const onChange = (name, value) => {
        imageSetting[name] = value;
        if (name === "colorScaleAuto" && value === true) {
            setColorScaleLimits();
        }
        props.onChange(imageSetting);
    };

    const debouncedOnChange = useDebounce(onChange, 800);

    const setColorScaleLimits = () => {
        if (imageSetting.colorScaleAuto === true) {
            imageSetting.colorScaleLowerLimit = fMin;
            imageSetting.colorScaleUpperLimit = fMax;
            colorScaleLowerLimit.set(fMin, true);
            colorScaleUpperLimit.set(fMax, true);
        }
    };

    return (
        <>
            {calcAreaName &&
                <>
                    <InputText
                        disabled={true}
                        value={calcAreaName}
                        title={intl.get("entry.plan.wattwaechter.name")} />
                    <Spacer variant="horizontal" />
                </>
            }
            <Grid template="2.2fr 1fr">
                <SelectSingle {...changeableFieldType.bind}
                    disabled={!isChangeableFieldType || disabled}
                    title={intl.get("entry.wattwaechterSetting.fieldType")}
                    toLabel={value => intl.get("enum.fieldType." + value)}>
                    <option value="EXHAUSTION_FACTOR_BLMSCHV">{intl.get("enum.fieldType.EXHAUSTION_FACTOR_BLMSCHV")}</option>
                    <option value="PROTECTION_AREA_BLMSCHV">{intl.get("enum.fieldType.PROTECTION_AREA_BLMSCHV")}</option>
                </SelectSingle>
                <Spinner {...slice.bind}
                    disabled={fieldMax.value || disabled}
                    min={0}
                    max={(sliceNum - 1)}
                    step={1}
                    title={intl.get("entry.plan.wattwaechter.slice")} />
            </Grid>
            <Spacer variant="horizontal" />
            <Grid columns="3">
                <Checkbox {...fieldMax.bind}
                    disabled={disabled}
                    title={intl.get("entry.plan.wattwaechter.fieldMax")} />
                <Checkbox {...logScale.bind}
                    disabled={disabled}
                    title={intl.get("entry.plan.wattwaechter.logScale")} />
                <Checkbox {...colorScaleAuto.bind}
                    disabled={disabled}
                    title={intl.get("entry.plan.wattwaechter.colorScaleAuto")} />
                <SelectSingle {...colorScale.bind}
                    disabled={disabled}
                    title={intl.get("entry.plan.wattwaechter.colorScale")}
                    toLabel={value => intl.get("enum.colorScale." + value)}>
                    <option value="RGB">{intl.get("enum.colorScale.RGB")}</option>
                    <option value="HOT">{intl.get("enum.colorScale.HOT")}</option>
                    <option value="GRAY">{intl.get("enum.colorScale.GRAY")}</option>
                </SelectSingle>
                <SelectSingle {...marker.bind}
                    disabled={disabled}
                    title={intl.get("entry.plan.wattwaechter.marker")}
                    toLabel={value => intl.get("enum.marker." + value)}>
                    <option value="NONE">{intl.get("enum.marker.NONE")}</option>
                    <option value="SMALL">{intl.get("enum.marker.SMALL")}</option>
                    <option value="LARGE">{intl.get("enum.marker.LARGE")}</option>
                </SelectSingle>
                <SelectSingle {...grid.bind}
                    disabled={disabled}
                    title={intl.get("entry.plan.wattwaechter.grid")}
                    toLabel={value => intl.get("enum.grid." + value)}>
                    <option value="NONE">{intl.get("enum.grid.NONE")}</option>
                    <option value="COARSE">{intl.get("enum.grid.COARSE")}</option>
                    <option value="FINE">{intl.get("enum.grid.FINE")}</option>
                </SelectSingle>
            </Grid>
            <Spacer variant="horizontal" />
            <Grid columns="2">
                <InputNumber {...colorScaleUpperLimit.bind}
                    fractionDigits={2}
                    disabled={disabled || colorScaleAuto.value}
                    title={intl.get("entry.plan.wattwaechter.colorScaleUpperLimit")} />
                <InputNumber {...colorScaleLowerLimit.bind}
                    fractionDigits={2}
                    disabled={disabled || colorScaleAuto.value}
                    title={intl.get("entry.plan.wattwaechter.colorScaleLowerLimit")} />
            </Grid>
        </>
    );
};
